import React, { useEffect, useState } from "react";
import { Inbox, Plus, Trash2, Info, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty, StatusPill } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TURNAROUND =
  "Your request has been submitted to the Dish Factory operations team for review. Please allow 48–72 hours for review and implementation. After submitting the request, please also notify our team by email, WhatsApp, or phone.";

export default function MyRequests() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [brand, setBrand] = useState(null);
  const [menu, setMenu] = useState([]);
  const [ings, setIngs] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => api.get("/change-requests").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/brands").then((r) => setBrand(r.data[0] || null));
    api.get("/menu-items").then((r) => setMenu(r.data));
    api.get("/ingredients").then((r) => setIngs(r.data));
  }, []);

  const emptyForm = (type) => ({ type, menu_item_id: "", name: "", sku: "", category: "Main", selling_price: 0, note: "", recipe: [] });
  const startNew = () => { setForm(emptyForm("create")); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickExisting = (id) => {
    const mi = menu.find((m) => m.id === id);
    if (!mi) { set("menu_item_id", ""); return; }
    setForm((f) => ({
      ...f, menu_item_id: id, name: mi.name, sku: mi.sku || "", category: mi.category, selling_price: mi.selling_price,
      recipe: (mi.recipe_detail || []).map((l) => ({ ingredient_id: l.ingredient_id, name: l.ingredient_name, qty: l.qty, unit: l.consumption_unit || "g" })),
    }));
  };

  const addLine = () => set("recipe", [...form.recipe, { ingredient_id: "", name: "", qty: 0, unit: "g" }]);
  const updLine = (i, patch) => set("recipe", form.recipe.map((l, x) => x === i ? { ...l, ...patch } : l));
  const rmLine = (i) => set("recipe", form.recipe.filter((_, x) => x !== i));

  const submit = async () => {
    setBusy(true);
    try {
      const proposed = {
        name: form.name, sku: form.sku, category: form.category, selling_price: Number(form.selling_price) || 0,
        recipe: form.recipe.map((l) => ({ ingredient_id: l.ingredient_id && l.ingredient_id !== "__new__" ? l.ingredient_id : null, name: l.name, qty: Number(l.qty) || 0, unit: l.unit })),
      };
      await api.post("/change-requests", { type: form.type, brand_id: brand.id, menu_item_id: form.menu_item_id || null, proposed, note: form.note });
      toast.success("Request submitted for approval");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resubmit = async (c) => {
    try { await api.patch(`/change-requests/${c.id}/status`, { status: "Submitted for Approval", note: "Resubmitted with updates" }); toast.success("Resubmitted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!list) return <Spinner />;
  const ingOpts = [{ value: "__new__", label: "➕ New ingredient" }, ...ings.map((i) => ({ value: i.id, label: i.name }))];

  return (
    <div>
      <PageHeader title="Menu Requests" subtitle="Submit new items or changes for Dish Factory approval">
        <Button onClick={startNew} data-testid="new-request-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold"><Plus className="h-4 w-4" /> New Request</Button>
      </PageHeader>

      <div className="card-surface p-4 mb-6 flex items-start gap-3 border-amber-500/30">
        <Info className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-300" data-testid="turnaround-banner">{TURNAROUND}</p>
      </div>

      {list.length === 0 ? <Empty icon={Inbox} title="No requests yet" hint="Submit your first menu change." /> : (
        <div className="space-y-3">
          {list.map((c) => (
            <div key={c.id} className="card-surface p-4" data-testid={`myreq-${c.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white truncate">{c.proposed?.name}</p>
                    <span className="text-xs text-muted-foreground capitalize">({c.type})</span>
                    {c.overdue && <span className="text-[10px] font-bold text-rose-400">OVERDUE</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground font-mono">Submitted {new Date(c.submitted_at).toLocaleString()}</p>
                </div>
                <StatusPill status={c.status} testid={`myreq-status-${c.id}`} />
              </div>
              {c.rejection_reason && c.status === "Rejected" && <p className="text-sm text-rose-400 mt-2">Rejected: {c.rejection_reason}</p>}
              {c.status === "More Information Required" && (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <p className="text-sm text-orange-400">Info requested: {c.more_info_note}</p>
                  <Button size="sm" variant="outline" onClick={() => resubmit(c)} data-testid={`resubmit-${c.id}`}>Resubmit</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {form && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border-border">
            <DialogHeader><DialogTitle>New Menu Request</DialogTitle></DialogHeader>
            <div className="flex gap-2 mb-2">
              {["create", "edit", "deactivate"].map((t) => (
                <button key={t} onClick={() => setForm({ ...emptyForm(t) })} data-testid={`req-type-${t}`}
                  className={`flex-1 text-xs py-2 rounded-lg border capitalize ${form.type === t ? "border-primary text-amber-300 bg-primary/10" : "border-border text-muted-foreground"}`}>{t}</button>
              ))}
            </div>
            {form.type !== "create" && (
              <FieldW label="Select existing menu item">
                <SelectBox value={form.menu_item_id} onChange={pickExisting} options={menu.map((m) => ({ value: m.id, label: m.name }))} testid="req-existing" />
              </FieldW>
            )}
            {form.type !== "deactivate" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <FieldW label="Item name" full><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="req-name" /></FieldW>
                <FieldW label="SKU"><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} /></FieldW>
                <FieldW label="Category"><Input value={form.category} onChange={(e) => set("category", e.target.value)} /></FieldW>
                <FieldW label="Selling price (AED)"><Input type="number" value={form.selling_price} onChange={(e) => set("selling_price", e.target.value)} /></FieldW>
              </div>
            )}
            {form.type !== "deactivate" && (
              <div className="mt-1">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-white">Recipe</p>
                  <Button variant="outline" size="sm" onClick={addLine} data-testid="req-add-line" className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add ingredient</Button>
                </div>
                <div className="space-y-2">
                  {form.recipe.map((l, i) => (
                    <div key={`${l.ingredient_id || "new"}-${i}`} className="flex items-center gap-2">
                      <div className="flex-1"><SelectBox value={l.ingredient_id || "__new__"} onChange={(v) => updLine(i, { ingredient_id: v, name: v !== "__new__" ? (ings.find((x) => x.id === v)?.name || l.name) : l.name })} options={ingOpts} /></div>
                      {(!l.ingredient_id || l.ingredient_id === "__new__") && <Input placeholder="New name" value={l.name} onChange={(e) => updLine(i, { name: e.target.value })} className="w-32" />}
                      <Input type="number" value={l.qty} onChange={(e) => updLine(i, { qty: e.target.value })} className="w-20" />
                      <Input value={l.unit} onChange={(e) => updLine(i, { unit: e.target.value })} className="w-16" />
                      <Button variant="ghost" size="icon" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <FieldW label="Note to operations team"><Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="req-note" /></FieldW>
            <p className="text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5">{TURNAROUND}</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy || (form.type !== "deactivate" && !form.name) || (form.type !== "create" && !form.menu_item_id)} data-testid="submit-request-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Submit for Approval</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
